/**
 * Servicio estático con métodos helper utilitarios
 * Para funciones comunes que no requieren inyección de dependencias
 */

import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarCustomComponent } from '../../components/snackbar-custom.component';
import { environment } from 'src/environments/environment';
import * as XLSX from 'xlsx';

@Injectable({ providedIn: 'root' })
export class Helpers {
  /**
   * Convierte un string base64 (dataURL) en un objeto File
   * @param base64 Cadena base64 tipo dataURL
   * @param filename Nombre del archivo resultante
   * @returns File
   */
  static base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }
  
  /**
   * Convierte un objeto File en un string base64 (dataURL)
   * @param file Objeto File a convertir
   * @param includePrefix Si es true incluye "data:image/png;base64,", si es false solo el string base64
   * @returns Promise con el string base64
   */
  static async fileToBase64(file: File, includePrefix: boolean = true, includeName: boolean = true): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (includePrefix) {
          // Inserta el nombre del archivo en el prefijo del dataURL
          if (includeName) {
            const base64WithFileName = result.replace(';base64,', `;name=${file.name};base64,`);
            resolve(base64WithFileName);
          } else {
            resolve(result);
          }
        } else {
          const base64 = result.split(',')[1];
          resolve(base64);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Limpia una URL de S3 para obtener el nombre original del archivo
   * Elimina el hash que S3 añade al final del nombre del archivo
   * @param url URL completa de S3
   * @returns Nombre original del archivo
   * @example
   * clearUrlS3('https://roadit-dev.s3.eu-west-1.amazonaws.com/clients/1/template-documents/imagen_1_53f764b5f26e45b6a4c3d27f5730be69.png')
   * // Retorna: 'imagen_1.png'
   */
  static clearUrlS3(url: string): string {
    // Extraer el nombre del archivo de la URL
    const fileName = url.split('/').pop() || '';
    
    // Dividir por el último punto para separar nombre y extensión
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return fileName;
    
    const nameWithoutExtension = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);
    
    // Eliminar el hash (último guión bajo y 32 caracteres hexadecimales)
    // Patrón: _[32 caracteres hexadecimales]
    const cleanName = nameWithoutExtension.replace(/_[a-f0-9]{32}$/i, '');
    
    return cleanName + extension;
  }

  /**
   * Instancia singleton para acceso a dependencias
   */
  
  private snackBar: MatSnackBar;
  static Instance: Helpers;
  constructor(snackBar: MatSnackBar) {
    this.snackBar = snackBar;
    Helpers.Instance = this;
  }

  /**
   * Mostrar un mensaje tipo toast usando MatSnackBar
   * @param message Mensaje a mostrar
   * @param type Tipo de mensaje: 'OK' | 'ERROR' | 'ALERT'
   * @param duration Duración en ms (opcional)
   */
  showToast(message: string, type: 'OK' | 'ERROR' | 'ALERT', duration: number = 3000): void {
    this.snackBar.openFromComponent(SnackbarCustomComponent, {
      data: { message, type },
      duration
    });
  }

  /**
   * Valida un archivo según formato y tamaño máximopref
   * @param file Archivo a validar
   * @param allowedFormats Array de extensiones permitidas (ej: ["JPG", "PNG"])
   * @param maxKb Tamaño máximo en KB
   * @returns { valid: boolean, error?: string, base64?: string }
   */
  static async validateAndReadFile(file: File, allowedFormats: string[], maxKb: number): Promise<{ valid: boolean, error?: string, base64?: string }> {
    const ext = file.name.split('.').pop()?.toUpperCase() || '';
    if (!allowedFormats.includes(ext)) {
      return { valid: false, error: `Formato no permitido (${ext}). Permitidos: ${allowedFormats.join(', ')}` };
    }
    const sizeKb = file.size / 1024;
    if (sizeKb > maxKb) {
      return { valid: false, error: `El archivo supera el tamaño máximo de ${maxKb} KB` };
    }
    // Leer como base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve({ valid: true, base64 });
      };
      reader.onerror = () => {
        resolve({ valid: false, error: 'Error leyendo el archivo' });
      };
      reader.readAsDataURL(file);
    });
  }
  
  /**
   * Verificar si un valor está vacío (null, undefined, string vacío, array vacío, objeto vacío)
   */
  static isEmpty(value: any): boolean {
    if (value == null  || value == undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }
  static isEmptyOrZero(value: any): boolean {
    if (value == null  || value == undefined) return true;
    if (this.isEmpty(value)) return true;
    if(Number.isNaN(value)) return true;
    try{
      const num = Number(value);
      return num == 0;
    }catch{
      return true;
    }
  }

  /**
   * Verificar si un valor NO está vacío
   */
  static isNotEmpty(value: any): boolean {
    return !this.isEmpty(value);
  }

  /**
   * Generar ID único
   */
  static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Formatear fecha a string legible
   */
  static formatDate(date: Date | string, locale: string = 'es-ES'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(locale);
  }

  /**
   * Formatear fecha y hora a string legible
   */
  static formatDateTime(date: Date | string, locale: string = 'es-ES'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString(locale);
  }

  /**
   * Convierte un número de horas decimal (base 10) a un string con formato hora (HH:mm)
   * @param hour Número decimal (ej: 1.5)
   * @returns String formateado (ej: "01:30")
   */
  static hourNumberToHourStr(hour: number): string {
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    
    const hStr = hours.toString().padStart(2, '0');
    const mStr = minutes.toString().padStart(2, '0');
    
    return `${hStr}:${mStr}`;
  }

  /**
   * Convierte un número de horas decimal (base 10) a un string descriptivo (X h Y min)
   * @param hour Número decimal (ej: 1.5)
   * @returns String formateado (ej: "1 h 30 min")
   */
  static HourNumberToString(hour: number): string {
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} h`;
    
    return `${hours} h ${minutes} min`;
  }

  /**
   * Convierte segundos a un string descriptivo (X h Y min)
   * @param seconds Número de segundos (ej: 7200 = 2 horas)
   * @returns String formateado (ej: "2 h 0 min")
   */
  static SecondsToHourString(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours === 0 && minutes === 0) return '0 min';
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} h`;
    
    return `${hours} h ${minutes} min`;
  }

  /**
   * Capitalizar primera letra de string
   */
  static capitalize(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Convertir string a kebab-case
   */
  static toKebabCase(str: string): string {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .toLowerCase()
      .replace(/\s+/g, '-');
  }

  /**
   * Convertir string a camelCase
   */
  static toCamelCase(str: string): string {
    return str
      .replace(/[-_\s]+(.)?/g, (_, char) => char ? char.toUpperCase() : '')
      .replace(/^[A-Z]/, char => char.toLowerCase());
  }

  /**
   * Clonar objeto profundamente
   */
  static deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
    if (obj instanceof Array) return obj.map(item => this.deepClone(item)) as unknown as T;
    if (typeof obj === 'object') {
      const copy = {} as T;
      Object.keys(obj).forEach(key => {
        (copy as any)[key] = this.deepClone((obj as any)[key]);
      });
      return copy;
    }
    return obj;
  }

  /**
   * Debounce function - retrasa la ejecución
   */
  static debounce<T extends (...args: any[]) => any>(
    func: T, 
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: number;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait) as any;
    };
  }

  /**
   * Throttle function - limita la frecuencia de ejecución
   */
  static throttle<T extends (...args: any[]) => any>(
    func: T, 
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Formatear número como moneda
   */
  static formatCurrency(
    amount: number, 
    currency: string = 'EUR', 
    locale: string = 'es-ES'
  ): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Convierte un valor (número o string) a un string formateado como euros (0.00 €)
   * Si el valor ya tiene el símbolo €, lo elimina para procesarlo.
   * Si no se puede convertir, devuelve "0.00 €"
   * @param value Valor a convertir
   * @returns String formateado (ej: "123.45 €")
   */
  static toEurString(value: string | number | null | undefined): string {
    if (value == null || value === undefined) return '0.00€';

    let cleanValue = value.toString();
    
    // Eliminar el símbolo € y espacios si existen
    cleanValue = cleanValue.replace('€', '').trim();
    
    // Reemplazar coma por punto para el parseo si es necesario
    if (cleanValue.includes(',') && !cleanValue.includes('.')) {
      cleanValue = cleanValue.replace(',', '.');
    } else if (cleanValue.includes(',') && cleanValue.includes('.')) {
      cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
    }

    const num = parseFloat(cleanValue);

    if (isNaN(num)) {
      return '0.00€';
    }

    return num.toFixed(2) + '€';
  }

  /**
   * Validar email
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Obtener valor anidado de objeto usando path con puntos
   */
  static getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Establecer valor anidado en objeto usando path con puntos
   */
  static setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      return current[key];
    }, obj);
    target[lastKey] = value;
  }

  /**
   * Guardar valor en localStorage con conversión automática a JSON
   */
  static setStorage(key: string, value: any): void {
    try {
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  /**
   * Leer valor de localStorage con conversión automática desde JSON
   */
  static getStorage<T = any>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue ?? null;
      }

      // Intentar parsear como JSON, si falla devolver como string
      try {
        
        return JSON.parse(item) as T;
      } catch {
        // Si no es JSON válido, devolver como string
        return item as unknown as T;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue ?? null;
    }
  }

  /**
   * Eliminar valor de localStorage
   */
  static removeStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }

  /**
   * Verificar si una clave existe en localStorage
   */
  static hasStorage(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error('Error checking localStorage:', error);
      return false;
    }
  }

  /**
   * Limpiar todo el localStorage
   */
  static clearStorage(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }

  /**
   * Obtener todas las claves del localStorage
   */
  static getStorageKeys(): string[] {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Error getting localStorage keys:', error);
      return [];
    }
  }

  /**
   * Guardar valor en sessionStorage con conversión automática a JSON
   */
  static setSessionStorage(key: string, value: any): void {
    try {
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      sessionStorage.setItem(key, serializedValue);
    } catch (error) {
      console.error('Error saving to sessionStorage:', error);
    }
  }

  /**
   * Leer valor de sessionStorage con conversión automática desde JSON
   */
  static getSessionStorage<T = any>(key: string, defaultValue?: T): T | null {
    try {
      const item = sessionStorage.getItem(key);
      if (item === null) {
        return defaultValue ?? null;
      }

      try {
        return JSON.parse(item) as T;
      } catch {
        return item as unknown as T;
      }
    } catch (error) {
      console.error('Error reading from sessionStorage:', error);
      return defaultValue ?? null;
    }
  }

  /**
   * Eliminar valor de sessionStorage
   */
  static removeSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from sessionStorage:', error);
    }
  }

  /**
   * Verificar si una clave existe en sessionStorage
   */
  static hasSessionStorage(key: string): boolean {
    try {
      return sessionStorage.getItem(key) !== null;
    } catch (error) {
      console.error('Error checking sessionStorage:', error);
      return false;
    }
  }

  /**
   * Limpiar todo el sessionStorage
   */
  static clearSessionStorage(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
  }

  /**
   * Busca un valor en sessionStorage primero, y si no existe, en localStorage.
   */
  static getAnyStorage<T = any>(key: string, defaultValue?: T): T | null {
    const sessionVal = this.getSessionStorage<T>(key);
    if (sessionVal !== null) return sessionVal;
    return this.getStorage<T>(key, defaultValue);
  }

  static isNullOrUndefined(value: any | undefined): boolean {
      return value === null || value === undefined;
  }
  
  /**
   * Obtener el tamaño usado del localStorage en bytes (aproximado)
   */
  static getStorageSize(): number {
    try {
      let total = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return total;
    } catch (error) {
      console.error('Error calculating localStorage size:', error);
      return 0;
    }
  }

  /**
   * Detectar si estamos en modo desarrollo
   */
  static isDevelopment(): boolean {
    // Método 1: Verificar hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
        return true;
      }
    }

    // Método 2: Verificar puerto de desarrollo
    if (typeof window !== 'undefined') {
      const port = window.location.port;
      const devPorts = ['4200', '3000', '8080', '5173', '3001', '8000'];
      if (devPorts.includes(port)) {
        return true;
      }
    }

    // Método 3: Verificar URL de desarrollo
    if (typeof window !== 'undefined') {
      const url = window.location.href;
      if (url.includes('localhost') || url.includes('dev.') || url.includes('staging.')) {
        return true;
      }
    }

    // Método 4: Variable de entorno inyectada en build
    try {
      // @ts-ignore - process puede no estar disponible en el navegador
      if (typeof process !== 'undefined' && process.env && process.env['NODE_ENV'] === 'development') {
        return true;
      }
    } catch {
      // Ignorar error si process no está disponible
    }

    return false;
  }

  /**
   * Detectar si estamos en modo producción
   */
  static isProduction(): boolean {
    return !this.isDevelopment();
  }

  /**
   * Detectar si estamos en modo staging
   */
  static isStaging(): boolean {
    if (typeof window !== 'undefined') {
      const url = window.location.href;
      return url.includes('staging.') || url.includes('test.') || url.includes('pre.');
    }
    return false;
  }
  static toSrc(source: string | File | null, def: string = "assets/images/sample_user_icon.png"): string {
    if (!source) return def;
    if (typeof source === 'string') {
      if (source.startsWith('http') || source.startsWith('data:')) {
        return source;
      }else{
        return environment.apiUrl + source;
      }
    }
    if (source instanceof File) {
      const blobUrl = URL.createObjectURL(source);
      return blobUrl;
    }
    return def;
  }
  static toUrl(source: string | null){
    if (!source) return '';
    if (!source.startsWith('http') ) {
      return environment.imgUrl + (source.startsWith("/") ? source.substring(1) : source);
    }
    return source;
  }
  /**
   * Obtener el entorno actual
   */
  static getEnvironment(): 'development' | 'staging' | 'production' {
    if (this.isStaging()) return 'staging';
    if (this.isDevelopment()) return 'development';
    return 'production';
  }

  /**
   * Verificar si las DevTools están abiertas (solo funciona en algunos navegadores)
   */
  static areDevToolsOpen(): boolean {
    if (typeof window === 'undefined') return false;
    
    let devtools = { open: false };
    const threshold = 160;

    // Método para Chrome/Firefox
    setInterval(() => {
      if (window.outerHeight - window.innerHeight > threshold || 
          window.outerWidth - window.innerWidth > threshold) {
        devtools.open = true;
      } else {
        devtools.open = false;
      }
    }, 500);

    return devtools.open;
  }

  /**
   * Obtener información del build/entorno
   */
  static getBuildInfo(): {
    environment: string;
    timestamp: string;
    hostname: string;
    userAgent: string;
    isDevelopment: boolean;
    isProduction: boolean;
    isStaging: boolean;
  } {
    return {
      environment: this.getEnvironment(),
      timestamp: new Date().toISOString(),
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      isDevelopment: this.isDevelopment(),
      isProduction: this.isProduction(),
      isStaging: this.isStaging()
    };
  }
  static getNowToDays(days: number, now?: Date): Date {
    now = now ? new Date(now) : new Date();
    now.setDate(now.getDate() + days);
    return now;
  }

  static isoDate(fecha: Date, end: boolean = false): string {
    const d = new Date(fecha);
    if(end){
      d.setHours(23, 59, 59, 999);
    }else{
      d.setHours(0, 0, 0, 0);
    }
    
    // Restamos el offset para que al hacer toISOString() 
    // no se mueva la hora según la zona horaria.
    const userOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - userOffset).toISOString();
  }
  /**
   * Verificar que los rangos de un array no se superpongan
   * Los objetos deben tener exactamente 2 propiedades: la primera es el inicio, la segunda el fin
   * @param ranges Array de objetos con dos propiedades (inicio y fin)
   * @returns true si no hay superposiciones, false si hay alguna superposición
   * @example
   * Helpers.checkNoRangeOverlap([{init: 4, end: 8}, {init: 9, end: 10}]) // true
   * Helpers.checkNoRangeOverlap([{init: 4, end: 8}, {init: 8, end: 10}]) // true (equal boundaries allowed)
   * Helpers.checkNoRangeOverlap([{init: 4, end: 8}, {init: 7, end: 10}]) // false
   * Helpers.checkNoRangeOverlap([{start: 4, finish: 8}, {start: 9, finish: 10}]) // true
   */
  static checkNoRangeOverlap<T extends Record<string, any>>(ranges: T[]): boolean {
    // Si el array está vacío o tiene un solo elemento, no hay superposiciones
    if (ranges.length < 1) return true;

    // Obtener las propiedades del primer objeto
    const keys = Object.keys(ranges[0]);
    if (keys.length !== 2) {
      console.error('Los objetos deben tener exactamente 2 propiedades');
      return false;
    }

    const [initKey, endKey] = keys;

    // Validar que todos los objetos tengan las mismas propiedades y valores válidos
    for (const range of ranges) {
      const rangeKeys = Object.keys(range);
      if (rangeKeys.length !== 2 || rangeKeys[0] !== initKey || rangeKeys[1] !== endKey) {
        console.error('Todos los objetos deben tener las mismas propiedades en el mismo orden');
        return false;
      }
      // Validar que init <= end
      if (range[initKey] > range[endKey]) {
        console.error(`Rango inválido (inicio > fin):`, range);
        return false;
      }
    }

    // Ordenar los rangos por el valor de inicio
    const sortedRanges = [...ranges].sort((a, b) => a[initKey] - b[initKey]);

    // Comparar cada rango con el siguiente
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const current = sortedRanges[i];
      const next = sortedRanges[i + 1];

      // Hay superposición si el fin del rango actual es estrictamente mayor al inicio del siguiente
      // Se permite que sean iguales (ej: 0-99 y 99-500) para evitar huecos con decimales
      if (current[endKey] > next[initKey] || (current[endKey] !== next[initKey] && current[endKey] !== next[initKey] - 1)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Lee un archivo (CSV o Excel) y lo convierte en un array de objetos.
   * La primera fila se utiliza para los nombres de las propiedades.
   */
  static async parseFileToObjects(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      // Manejo de Excel (.xlsx, .xls)
      if (file.name.match(/\.(xlsx|xls)$/i)) {
        reader.onload = (e: any) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const rawResult = XLSX.utils.sheet_to_json(worksheet) as any[];

            // Obtenemos las cabeceras para saber cuál es la primera columna
            const headerRow = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[];
            const firstColumn = headerRow ? headerRow[0] : null;

            // Filtrar filas: debe haber dato al menos en la primera columna siempre
            const result = rawResult.filter(row => {
              if (!firstColumn) return false;
              const val = row[firstColumn];
              return val !== null && val !== undefined && val.toString().trim() !== '';
            });

            resolve(result);
          } catch (error) {
            reject(new Error('Error al procesar el archivo Excel. Asegúrese de que el formato es correcto.'));
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo Excel.'));
        reader.readAsArrayBuffer(file);
      } 
      // Manejo de CSV (y otros formatos de texto)
      else {
        reader.onload = (e: any) => {
          try {
            const content = e.target.result;
            const result = this.csvToObjects(content);
            resolve(result);
          } catch (error) {
            reject(new Error('Error al procesar el archivo CSV.'));
          }
        };
        reader.onerror = () => reject(new Error('Error al leer el archivo.'));
        reader.readAsText(file);
      }
    });
  }

  /**
   * Convierte texto CSV a un array de objetos
   */
  private static csvToObjects(csvText: string): any[] {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return [];

    // Detectar separador común (coma o punto y coma)
    const firstLine = lines[0];
    const separator = firstLine.includes(';') ? ';' : ',';
    
    const headers = firstLine.split(separator).map(h => h.trim().replace(/^["']|["']$/g, ''));
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator).map(v => v.trim().replace(/^["']|["']$/g, ''));
      
      // Regla: Debe haber dato al menos en la primera columna siempre
      if (values.length > 0 && values[0] !== '') {
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || null;
        });
        data.push(row);
      }
    }

    return data;
  }

  /**
   * Obtiene el valor de un objeto dado un path de propiedades (ej: "driver.name")
   * @param obj Objeto base
   * @param path Path de la propiedad
   * @returns Valor encontrado o undefined
   */
  static getValueByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    
    // Si el path no contiene puntos, intentamos acceso directo
    if (!path.includes('.')) {
      return obj[path];
    }

    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current === null || current === undefined) return undefined;
      current = current[part];
    }

    return current;
  }
}
